// components/sections/PaymentSection.js
import React, { useEffect, useCallback } from 'react';
import InteractiveField from '../common/InteractiveField';
import { convertAmountToWords, getRublesForm } from '../../utils/currency';
import { getMoneyTransferActNumber } from '../../utils/attachmentNumbers';

/**
 * Компонент для раздела "4. Условия оплаты и порядок расчетов".
 * @param {object} data - Объект состояния для этого раздела.
 * @param {function} onChange - Функция для обновления состояния.
 * @param {object} contractData - Все данные договора для вычисления номеров приложений.
 */
const PaymentSection = ({ data, onChange, showValidation = false, contractData }) => {
  // Безопасная деструктуризация с фолбэками
  const {
    monthlyAmount = { amount: '', amountWords: '' },
    revisionConditions = { type: 'none' }, // 'none', 'annual', 'fixed'
    paymentMethod = { type: 'bank', conditions: 'current' }, // 'bank', 'cash'
    utilityPayment = { type: 'tenant' }, // 'tenant', 'tenantMeters', 'landlord'
    internetPayment = { enabled: false, payer: 'landlord' }, // 'landlord', 'tenant'
    securityDeposit = { enabled: false, amount: '', amountWords: '', returnType: 'return' }, // 'return', 'credit'
    agentCommission = { enabled: false, agentName: '' }
  } = data;

  const handleNestedChange = useCallback((section, field, value) => {
    const newData = { ...data, [section]: { ...data[section], [field]: value } };
    onChange(newData);
  }, [data, onChange]);

  // Эффект для автоматического обновления текстового представления суммы
  useEffect(() => {
    if (monthlyAmount.amount) {
      const words = convertAmountToWords(monthlyAmount.amount);
      if (words && words !== monthlyAmount.amountWords) {
        handleNestedChange('monthlyAmount', 'amountWords', words);
      }
    }
  }, [monthlyAmount.amount, monthlyAmount.amountWords, handleNestedChange]);

  // Эффект для автоматического обновления текстового представления суммы обеспечительного платежа
  useEffect(() => {
    if (securityDeposit.enabled && securityDeposit.amount) {
      const words = convertAmountToWords(securityDeposit.amount);
      if (words && words !== securityDeposit.amountWords) {
        handleNestedChange('securityDeposit', 'amountWords', words);
      }
    }
  }, [securityDeposit.enabled, securityDeposit.amount, securityDeposit.amountWords, handleNestedChange]);

  // Получаем номер приложения для акта передачи денежных средств динамически
  const moneyTransferActNumber = getMoneyTransferActNumber(contractData);

  // Счетчик для динамической нумерации
  let clauseCounter = 1;

  return (
    <div className="document-preview-editor">
      <h2 className="special-section-title" style={{ width: '100%', display: 'block', textAlign: 'center', marginBottom: '24px' }}>
        4. Условия оплаты и порядок расчетов
      </h2>

      {/* Пункт 4.1 - Размер ежемесячной платы */}
      <p className="document-preamble">
        <b>4.{clauseCounter}.</b> Размер ежемесячной платы за наем Помещения, составляет{' '}
        <InteractiveField
          value={monthlyAmount.amount}
          onChange={(v) => handleNestedChange('monthlyAmount', 'amount', v)}
          placeholder="50000"
          type="number"
          integerOnly={true}
          isRequired={true}
          showValidation={showValidation}
          validationFieldId="payment-monthlyAmount"
        />{' '}
        {monthlyAmount.amount && (
          <>
            <span className="amount-words-wrapper">
              (<span>
                {monthlyAmount.amountWords || convertAmountToWords(monthlyAmount.amount)}
              </span>)
            </span>
          </>
        )}{' '}
        {getRublesForm(monthlyAmount.amount)}.
      </p>

      {/* Пункт 4.2 - Условия пересмотра размера платы */}
      <div className="configurable-section dynamic-content-block active-purple">
        {revisionConditions.type === 'annual' && (
          <p className="document-preamble">
            <b>4.{++clauseCounter}.</b> Размер ежемесячной платы за наем Помещения может быть пересмотрен по соглашению Сторон один раз в год.
          </p>
        )}

        {revisionConditions.type === 'fixed' && (
          <p className="document-preamble">
            <b>4.{++clauseCounter}.</b> Размер ежемесячной платы за наем Помещения изменению не подлежит в течение срока действия Договора.
          </p>
        )}

        <div className="option-pills-group" style={{ marginTop: '16px' }}>
          <button
            className={`option-pill ${revisionConditions.type === 'annual' ? 'selected' : ''}`}
            onClick={() => handleNestedChange('revisionConditions', 'type', 'annual')}
          >
            Предусмотрена возможность изменения
          </button>
          <button
            className={`option-pill ${revisionConditions.type === 'fixed' ? 'selected' : ''}`}
            onClick={() => handleNestedChange('revisionConditions', 'type', 'fixed')}
          >
            Изменению не подлежит
          </button>
        </div>
      </div>

      {/* Пункт 4.3 - Способ оплаты */}
      <div className="configurable-section dynamic-content-block active-purple">
        {paymentMethod.type === 'bank' && (
          <div>
            {paymentMethod.conditions === 'current' && (
              <p className="document-preamble">
                <b>4.{++clauseCounter}.</b> Наниматель ежемесячно до{' '}
                <InteractiveField
                  value={paymentMethod.dayOfMonth || ''}
                  onChange={(v) => handleNestedChange('paymentMethod', 'dayOfMonth', v)}
                  placeholder="10"
                  type="number"
                  isRequired={true}
                />{' '}
                числа вносит плату за Помещение за текущий месяц на банковский счет или карту Наймодателя по реквизитам, указанным в настоящем Договоре.
              </p>
            )}

            {paymentMethod.conditions === 'previous' && (
              <p className="document-preamble">
                <b>4.{++clauseCounter}.</b> Наниматель ежемесячно до{' '}
                <InteractiveField
                  value={paymentMethod.dayOfMonth || ''}
                  onChange={(v) => handleNestedChange('paymentMethod', 'dayOfMonth', v)}
                  placeholder="10"
                  type="number"
                  isRequired={true}
                />{' '}
                числа вносит плату за Помещение за прошедший месяц на банковский счет или карту Наймодателя по реквизитам, указанным в настоящем Договоре.
              </p>
            )}

            {paymentMethod.conditions === 'lump' && (
              <p className="document-preamble">
                <b>4.{++clauseCounter}.</b> Наниматель вносит плату за Помещение единовременно за весь период найма осуществляя предоплату до{' '}
                <InteractiveField
                  value={paymentMethod.prepaymentDate || ''}
                  onChange={(v) => handleNestedChange('paymentMethod', 'prepaymentDate', v)}
                  placeholder="01.09.2025"
                  isRequired={true}
                />{' '}
                на банковский счет или карту Наймодателя по реквизитам, указанным в настоящем Договоре.
              </p>
            )}

            <div className="option-pills-group" style={{ marginTop: '16px' }}>
              <button
                className={`option-pill ${paymentMethod.type === 'bank' ? 'selected' : ''}`}
                onClick={() => handleNestedChange('paymentMethod', 'type', 'bank')}
              >
                Безналичный расчет
              </button>
              <button
                className={`option-pill ${paymentMethod.type === 'cash' ? 'selected' : ''}`}
                onClick={() => handleNestedChange('paymentMethod', 'type', 'cash')}
              >
                Наличный расчет
              </button>
            </div>

            <div className="option-pills-group" style={{ marginTop: '16px' }}>
              <button
                className={`option-pill ${paymentMethod.conditions === 'current' ? 'selected' : ''}`}
                onClick={() => handleNestedChange('paymentMethod', 'conditions', 'current')}
              >
                Расчет за текущий месяц
              </button>
              <button
                className={`option-pill ${paymentMethod.conditions === 'previous' ? 'selected' : ''}`}
                onClick={() => handleNestedChange('paymentMethod', 'conditions', 'previous')}
              >
                Расчет за предыдущий месяц
              </button>
              <button
                className={`option-pill ${paymentMethod.conditions === 'lump' ? 'selected' : ''}`}
                onClick={() => handleNestedChange('paymentMethod', 'conditions', 'lump')}
              >
                Расчет за весь период найма
              </button>
            </div>
          </div>
        )}

        {paymentMethod.type === 'cash' && (
          <div>
            {paymentMethod.conditions === 'current' && (
              <p className="document-preamble">
                <b>4.{++clauseCounter}.</b> Наниматель ежемесячно до{' '}
                <InteractiveField
                  value={paymentMethod.dayOfMonth || ''}
                  onChange={(v) => handleNestedChange('paymentMethod', 'dayOfMonth', v)}
                  placeholder="10"
                  type="number"
                  isRequired={true}
                />{' '}
                числа вносит плату за Помещение за текущий месяц посредством передачи наличных денежных средств Наймодателю по акту приема-передачи денежных средств (Приложение № {moneyTransferActNumber}).
              </p>
            )}

            {paymentMethod.conditions === 'previous' && (
              <p className="document-preamble">
                <b>4.{++clauseCounter}.</b> Наниматель ежемесячно до{' '}
                <InteractiveField
                  value={paymentMethod.dayOfMonth || ''}
                  onChange={(v) => handleNestedChange('paymentMethod', 'dayOfMonth', v)}
                  placeholder="10"
                  type="number"
                  isRequired={true}
                />{' '}
                числа вносит плату за Помещение за прошедший месяц посредством передачи наличных денежных средств Наймодателю по акту приема-передачи денежных средств (Приложение № {moneyTransferActNumber}).
              </p>
            )}

            {paymentMethod.conditions === 'lump' && (
              <p className="document-preamble">
                <b>4.{++clauseCounter}.</b> Наниматель вносит плату за Помещение единовременно за весь период найма осуществляя предоплату до{' '}
                <InteractiveField
                  value={paymentMethod.prepaymentDate || ''}
                  onChange={(v) => handleNestedChange('paymentMethod', 'prepaymentDate', v)}
                  placeholder="01.09.2025"
                  isRequired={true}
                />{' '}
                посредством передачи наличных денежных средств Наймодателю по акту приема-передачи денежных средств (Приложение № {moneyTransferActNumber}).
              </p>
            )}

            <div className="option-pills-group" style={{ marginTop: '16px' }}>
              <button
                className={`option-pill ${paymentMethod.type === 'bank' ? 'selected' : ''}`}
                onClick={() => handleNestedChange('paymentMethod', 'type', 'bank')}
              >
                Безналичный расчет
              </button>
              <button
                className={`option-pill ${paymentMethod.type === 'cash' ? 'selected' : ''}`}
                onClick={() => handleNestedChange('paymentMethod', 'type', 'cash')}
              >
                Наличный расчет
              </button>
            </div>

            <div className="option-pills-group" style={{ marginTop: '16px' }}>
              <button
                className={`option-pill ${paymentMethod.conditions === 'current' ? 'selected' : ''}`}
                onClick={() => handleNestedChange('paymentMethod', 'conditions', 'current')}
              >
                Расчет за текущий месяц
              </button>
              <button
                className={`option-pill ${paymentMethod.conditions === 'previous' ? 'selected' : ''}`}
                onClick={() => handleNestedChange('paymentMethod', 'conditions', 'previous')}
              >
                Расчет за предыдущий месяц
              </button>
              <button
                className={`option-pill ${paymentMethod.conditions === 'lump' ? 'selected' : ''}`}
                onClick={() => handleNestedChange('paymentMethod', 'conditions', 'lump')}
              >
                Расчет за весь период найма
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Пункт 4.4 - Общие условия по ЖКУ (ссылка на 4.6 только при включённых интернет-услугах) */}
      <p className="document-preamble">
        <b>4.{++clauseCounter}.</b> Ежемесячная плата за наем Помещения не включает в себя плату за услуги, указанные в п. 4.5{internetPayment.enabled ? ', 4.6' : ''}. настоящего Договора.
      </p>

      {/* Пункт 4.5 - Условия оплаты ЖКУ */}
      <div className="configurable-section dynamic-content-block active-purple">
        {utilityPayment.type === 'tenant' && (
          <p className="document-preamble">
            <b>4.{++clauseCounter}.</b> Наниматель ежемесячно за свой счет оплачивает жилищно-коммунальные услуги, а также взносы на капитальный ремонт.
          </p>
        )}

        {utilityPayment.type === 'tenantMeters' && (
          <p className="document-preamble">
            <b>4.{++clauseCounter}.</b> Наниматель ежемесячно передает денежные средства Наймодателю за потребленные коммунальные услуги согласно показаниям приборов учета в соответствии с установленными тарифами ЖКХ. Остальные ЖКУ Наймодатель оплачивает за свой счет, а также взносы на капитальный ремонт.
          </p>
        )}

        {utilityPayment.type === 'landlord' && (
          <p className="document-preamble">
            <b>4.{++clauseCounter}.</b> Наймодатель ежемесячно за свой счет оплачивает жилищно-коммунальные услуги, а также взносы на капитальный ремонт.
          </p>
        )}

        <div className="option-pills-group" style={{ marginTop: '16px' }}>
          <button
            className={`option-pill ${utilityPayment.type === 'tenant' ? 'selected' : ''}`}
            onClick={() => handleNestedChange('utilityPayment', 'type', 'tenant')}
          >
            Наниматель оплачивает ЖКУ
          </button>
          <button
            className={`option-pill ${utilityPayment.type === 'tenantMeters' ? 'selected' : ''}`}
            onClick={() => handleNestedChange('utilityPayment', 'type', 'tenantMeters')}
          >
            Наниматель только приборы учета
          </button>
          <button
            className={`option-pill ${utilityPayment.type === 'landlord' ? 'selected' : ''}`}
            onClick={() => handleNestedChange('utilityPayment', 'type', 'landlord')}
          >
            Наймодатель оплачивает ЖКУ
          </button>
        </div>
      </div>

      {/* Пункт 4.6 - Условия оплаты интернет услуг (опционально) */}
      <div className="configurable-section dynamic-content-block active-green">
        <div className="option-pills-group" style={{ marginBottom: '16px' }}>
          <button
            className={`option-pill-toggle ${internetPayment.enabled ? 'selected' : ''}`}
            onClick={() => handleNestedChange('internetPayment', 'enabled', !internetPayment.enabled)}
          >
            {internetPayment.enabled ? '✓ Интернет услуги' : '+ Интернет услуги'}
          </button>
        </div>

        {internetPayment.enabled && (
          <div>
            {internetPayment.payer === 'landlord' && (
              <p className="document-preamble">
                <b>4.{++clauseCounter}.</b> Наймодатель за свой счет оплачивает услуги по предоставлению доступа в Интернет и к домашнему телевидению.
              </p>
            )}

            {internetPayment.payer === 'tenant' && (
              <p className="document-preamble">
                <b>4.{++clauseCounter}.</b> Наниматель за свой счет оплачивает услуги по предоставлению доступа в Интернет и к домашнему телевидению.
              </p>
            )}

            <div className="option-pills-group" style={{ marginTop: '16px' }}>
              <button
                className={`option-pill ${internetPayment.payer === 'landlord' ? 'selected' : ''}`}
                onClick={() => handleNestedChange('internetPayment', 'payer', 'landlord')}
              >
                Оплачивает Наймодатель
              </button>
              <button
                className={`option-pill ${internetPayment.payer === 'tenant' ? 'selected' : ''}`}
                onClick={() => handleNestedChange('internetPayment', 'payer', 'tenant')}
              >
                Оплачивает Наниматель
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Пункт 4.7 - Оплата во время отсутствия */}
      <p className="document-preamble">
        <b>4.{++clauseCounter}.</b> В случае временного отсутствия Нанимателя и (или) лиц, проживающих с ним в Помещении, размер платы, указанный в п. 4.1 настоящего Договора, изменению не подлежит.
      </p>

      {/* Пункт 4.8-4.10 - Обеспечительный платеж и дополнительные условия */}
      <div className="configurable-section dynamic-content-block active-green">
        <div className="option-pills-group" style={{ marginBottom: '16px' }}>
          <button
            className={`option-pill-toggle ${securityDeposit.enabled ? 'selected' : ''}`}
            onClick={() => handleNestedChange('securityDeposit', 'enabled', !securityDeposit.enabled)}
          >
            {securityDeposit.enabled ? '✓ Обеспечительный платеж предусмотрен' : '+ Обеспечительный платеж предусмотрен'}
          </button>
        </div>

        {securityDeposit.enabled && (
          <div>
            <p className="document-preamble">
              <b>4.{++clauseCounter}.</b> Наниматель обязан внести обеспечительный платеж в размере{' '}
              <InteractiveField
                value={securityDeposit.amount}
                onChange={(v) => handleNestedChange('securityDeposit', 'amount', v)}
                placeholder="50000"
                type="number"
                integerOnly={true}
                isRequired={true}
              />{' '}
              {securityDeposit.amount && (
                <>
                  <span className="amount-words-wrapper">
                    (<span>
                      {securityDeposit.amountWords || convertAmountToWords(securityDeposit.amount)}
                    </span>)
                  </span>
                </>
              )}{' '}
              {getRublesForm(securityDeposit.amount)} вместе с первым платежом за наем Помещения.
            </p>

            <p className="document-preamble">
              <b>4.{++clauseCounter}.</b> Наймодатель из средств обеспечительного платежа возмещает свои убытки в случае причинения ущерба Помещению или имуществу по вине Нанимателя, а также взымает неустойку в случае нарушения условий настоящего Договора.
            </p>

            {securityDeposit.returnType === 'return' && (
              <p className="document-preamble">
                <b>4.{++clauseCounter}.</b> При прекращении действия настоящего Договора обеспечительный платеж подлежит возврату Нанимателю в{' '}
                <InteractiveField
                  value={securityDeposit.returnDays || ''}
                  onChange={(v) => handleNestedChange('securityDeposit', 'returnDays', v)}
                  placeholder="10"
                  type="number"
                  isRequired={true}
                />
                {'-дневный срок, за вычетом задолженности Нанимателя за наем Помещения и предоставленные услуги в соответствии с условиями настоящего Договора.'}
              </p>
            )}

            {securityDeposit.returnType === 'credit' && (
              <p className="document-preamble">
                <b>4.{++clauseCounter}.</b> При прекращении действия настоящего Договора обеспечительный платеж подлежит зачету в счет платы за последний месяц найма Помещения в соответствии с условиями настоящего Договора.
              </p>
            )}

            <div className="option-pills-group" style={{ marginTop: '16px' }}>
              <button
                className={`option-pill ${securityDeposit.returnType === 'return' ? 'selected' : ''}`}
                onClick={() => handleNestedChange('securityDeposit', 'returnType', 'return')}
              >
                Подлежит возврату
              </button>
              <button
                className={`option-pill ${securityDeposit.returnType === 'credit' ? 'selected' : ''}`}
                onClick={() => handleNestedChange('securityDeposit', 'returnType', 'credit')}
              >
                Засчитывается в счет последнего месяца
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Пункт 4.11 - Комиссия агента (опционально) */}
      <div className="configurable-section dynamic-content-block active-green">
        <div className="option-pills-group" style={{ marginBottom: '16px' }}>
          <button
            className={`option-pill-toggle ${agentCommission.enabled ? 'selected' : ''}`}
            onClick={() => handleNestedChange('agentCommission', 'enabled', !agentCommission.enabled)}
          >
            {agentCommission.enabled ? '✓ Комиссия агента' : '+ Комиссия агента'}
          </button>
        </div>

        {agentCommission.enabled && (
          <p className="document-preamble">
            <b>4.{++clauseCounter}.</b> Наниматель оплачивает комиссию агента за подбор Помещения в день подписания настоящего Договора согласно реквизитам, предоставленным агентом{' '}
            <InteractiveField
              value={agentCommission.agentName}
              onChange={(v) => handleNestedChange('agentCommission', 'agentName', v)}
              placeholder="ФИО агента"
              isRequired={true}
            />.
          </p>
        )}
      </div>
    </div>
  );
};

export default PaymentSection;
